// @flow
import { getLogger } from "@cld/upload-widget";
import createBoundActionCreators, {
	createCustomCreator,
	createDispatcher,
} from "common/actionsBase";
import rootBindActions, { types as rootTypes } from "root/actions";
import { fileSelect } from "tabs/common/actionMethods";
import getDropbox from "../service";
import dbConsts from "../data/consts";
import {
	selectAuthData,
	selectIsIdSelected,
	selectReachedMaxSelection,
	selectCurrentContext,
	selectContextItems,
	selectMultipleIdsInRange,
	selectAvailableUntilMax,
} from "../selectors";

type SelectParams = {|
    path: ?string,
    extend: ?boolean,
    items?: Object[],
    cursor?: number,
    hasMore?: boolean,
|};

type SearchParams = {| term: string, items: Object[], cursor: ?number |};

const logger = getLogger();

export const types = {
	INIT_AUTH: "DB_INIT_AUTH",
	SET_AUTH_INFO: "DB_SET_AUTH_INFO",
	SET_FILE_SELECTION: "DB_SET_FILE_SELECTION",
	SET_MULTI_FILE_SELECTION: "DB_SET_MUTLI_FILE_SELECTION",
	GET_CONTEXT_FILES: "DB_GET_CONTEXT_FILES",
	SET_FILES_RETRIEVE_START: "DB_SET_FILES_RETRIEVE_START",
	SET_FILES_RETRIEVE_FAILED: "DB_SET_FILES_RETRIEVE_FAILED",
	SET_FILES_RETRIEVE_SUCCESS: "DB_SET_FILES_RETRIEVE_SUCCESS",
	SET_ACCOUNT_INFO_REQUESTED: "DB_SET_ACCOUNT_INFO_REQUESTED",
	SET_ACCOUNT_INFO_FAILED: "DB_SET_ACCOUNT_INFO_FAILED",
	SET_ACCOUNT_INFO_SUCCESS: "DB_SET_ACCOUNT_INFO_SUCCESS",
	SET_USER_LOGOUT_SUCCESS: "DB_SET_USER_LOGOUT_SUCCESS",
	SET_SEARCH_TERM: "DB_SET_SEARCH_TERM",
	SET_LOADED_THUMBNAILS: "DB_SET_LOADED_THUMBNAILS",
	SET_THUMBNAILS: "DB_SET_THUMBNAILS",
	DO_LOGOUT: "DB_DO_LOGOUT",
	DO_FILE_SEARCH: "DB_DO_FILE_SEARCH",
	CLEAR_FILES_FOR_CONTEXT: "DB_CLEAR_FILES_FOR_CONTEXT",
	CLEAR_IMAGE_SELECTION: "DB_CLEAR_IMAGE_SELECTION",
	CLEAR_ALL_SELECTION: "DB_CLEAR_ALL_SELECTION",
};

type authInfoType = { token: ?string };

const contextRetrievers = {
	[dbConsts.CONTEXTS.FOLDER](token: string, params: SelectParams) {
		return getDropbox().getFoldersList({ token, ...params });
	},

	[dbConsts.CONTEXTS.SEARCH](token: string, params: SearchParams) {
		return getDropbox().getSearchResults({ token, ...params });
	},

	[dbConsts.CONTEXTS.RECENT](token: string, params: SearchParams) {
		return getDropbox().getRecents({ token, ...params });
	},
};

const getCurrentAccount = async (authInfo: authInfoType, dispatcher: Function) => {
	logger.log("[dropboxTab.actions]: about to retrieve account info");

	try {
		dispatcher(types.SET_ACCOUNT_INFO_REQUESTED);

		const account = await getDropbox().getAccountInfo(authInfo);
		logger.log("[dropboxTab.actions]: retrieved account info: ", account);

		dispatcher(types.SET_ACCOUNT_INFO_SUCCESS, { account });
	} catch (err) {
		logger.error("[dropboxTab.actions]: failed to get account info", err);
		dispatcher(types.SET_ACCOUNT_INFO_FAILED);
	}
};

const setAuthInfo = (authInfo: authInfoType, dispatch: Function) => {
	const dispatcher = createDispatcher(dispatch);

	dispatcher(types.SET_AUTH_INFO, authInfo);

	if (authInfo && authInfo.token) {
		getCurrentAccount(authInfo, dispatcher);
	}
};

const loadItemsThumbs = async (context: string, items: Object[], dispatcher: Function) => {
	const thumbs = items && items.length ? await getDropbox().retrieveItemsThumbs(items) : [];

	if (thumbs.length) {
		dispatcher(types.SET_THUMBNAILS, { context, thumbs });
	}
};

export default createBoundActionCreators(types, {

	[types.CLEAR_IMAGE_SELECTION]: createCustomCreator((context: string, dispatch: Function) => {
		createDispatcher(dispatch)([
			[types.CLEAR_IMAGE_SELECTION, { context }],
			[rootTypes.CLEAR_MAX_SELECT_REACHED]]);
	}),

	[types.INIT_AUTH]: createCustomCreator((cloudName, dispatch: Function) => setAuthInfo(getDropbox().getAuthInfo(cloudName), dispatch)),

	[types.SET_AUTH_INFO]: createCustomCreator(setAuthInfo),

	[types.GET_CONTEXT_FILES]: createCustomCreator(
		async (context: string, params: ?SelectParams, dispatch: Function, getState: Function) => {
			const dispatcher = createDispatcher(dispatch);
			logger.log(`[dropboxTab.actions]: about to retrieve files from db for context: ${context}`);

			try {
				dispatcher(types.SET_FILES_RETRIEVE_START, { context });

				if (params && params.extend) {
					const contextData = selectContextItems(getState());

					params = {
						...params,
						items: contextData.items,
						cursor: contextData.cursor,
						hasMore: contextData.hasMore,
					};
				} else {
					dispatcher(types.CLEAR_FILES_FOR_CONTEXT, { context });
				}

				const { token } = selectAuthData(getState());
				const result = await contextRetrievers[context](token, params, dispatcher);

				logger.log(`[dropboxTab.actions]: finished retrieving files for context: ${context}`, result);

				dispatcher(types.SET_FILES_RETRIEVE_SUCCESS, { context, ...result });

				loadItemsThumbs(context, result.items, dispatcher); // load thumbs asynchronously since its a heavy operation
			} catch (err) {
				logger.devError("[dropboxTab.actions]: encountered error when trying to retrieve context photos", err);
				dispatcher(types.SET_FILES_RETRIEVE_FAILED, { context });
			}
		},
	),

	[types.SET_FILE_SELECTION]: createCustomCreator(
		({ id, e, multiple }: { id: string, e: MouseEvent, multiple: boolean },
			dispatch: Function, getState: Function) => {
			const { context } = selectCurrentContext(getState());

			return fileSelect({
				id,
				e,
				types: { ...rootTypes, ...types },
				rootBindActions,
				selectIsIdSelected,
				selectReachedMaxSelection,
				selectMultipleIdsInRange,
				selectAvailableUntilMax,
				selectionParams: [context],
				multiple,
			}, dispatch, getState);
		},
	),

	[types.DO_LOGOUT]: createCustomCreator(
		async (dispatch: Function, getState: Function) => {
			const dispatcher = createDispatcher(dispatch);
			const authData = selectAuthData(getState());

			try {
				logger.log("[dropboxTab.actions]: about to log out dropbox user");
				await getDropbox().logout(authData);
				dispatcher(types.SET_USER_LOGOUT_SUCCESS);
			} catch (err) {
				logger.error("[dropboxTab.actions]: failed to log out user", err);
			}
		},
	),
});
